import { useReminderHook } from "@/hooks/use-reminder.hook.ts";
import {
  buildReminderPayload,
  encodeReminderPreset,
  ReminderPayload,
  regularReminderPresets,
  reminderChoiceFromPayload,
  reminderOffChoice,
} from "@/pages/esign/esign-reminder.util.ts";
import { EsignReminderCustomModal } from "@/pages/esign/widgets/EsignReminderCustomModal.tsx";
import { ReminderMode, ReminderPeriod } from "@/types/reminder.types.ts";
import { SelectOptionItem, useModals } from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type SelectValue = string | number | null | undefined;

export function useEsignReminder(initialPayload?: ReminderPayload) {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { getReminderModeTitle, getReminderModeDescription } = useReminderHook();

  const initial = reminderChoiceFromPayload(initialPayload ?? { reminder_mode: null });
  const [choice, setChoice] = useState<string | null>(initial.choice);
  const [customFrequency, setCustomFrequency] = useState(initial.customFrequency);
  const [customPeriod, setCustomPeriod] = useState<ReminderPeriod>(initial.customPeriod);

  const everyReminderTitle = (frequency: number, period: ReminderPeriod): string => {
    const key =
      period === ReminderPeriod.week
        ? "core:esign_reminder_every_weeks"
        : period === ReminderPeriod.month
          ? "core:esign_reminder_every_months"
          : "core:esign_reminder_every_days";
    return t(key, { count: frequency });
  };

  const options: SelectOptionItem[] = [
    ...[ReminderMode.light, ReminderMode.medium, ReminderMode.hard].map((mode) => ({
      id: mode,
      title: getReminderModeTitle(mode),
      caption: getReminderModeDescription(mode),
      group: t("core:esign_reminders_smart"),
    })),
    ...regularReminderPresets.map((preset) => ({
      id: encodeReminderPreset(preset.frequency, preset.period),
      title: everyReminderTitle(preset.frequency, preset.period),
      group: t("core:esign_reminders_regular"),
    })),
    { id: reminderOffChoice, title: t("common:off"), group: t("core:esign_reminders_regular") },
    { type: "divider" },
    {
      id: ReminderMode.custom,
      title:
        choice === ReminderMode.custom
          ? everyReminderTitle(customFrequency, customPeriod)
          : t("core:esign_reminder_custom"),
    },
  ];

  const onChange = (value: SelectValue) => {
    if (value === ReminderMode.custom) {
      openModal(EsignReminderCustomModal, {
        frequency: customFrequency,
        period: customPeriod,
        onSave: (frequency: number, period: ReminderPeriod) => {
          setCustomFrequency(frequency);
          setCustomPeriod(period);
          setChoice(ReminderMode.custom);
        },
      });
      return;
    }
    setChoice(value != null ? String(value) : null);
  };

  const buildPayload = (): ReminderPayload =>
    buildReminderPayload(choice, customFrequency, customPeriod);

  return { options, value: choice, onChange, buildPayload };
}
